// Phase 8 Plan 02 Task 2 — visibleWhen evaluator (LIFTED from apps/web).
//
// Pure TS — no React, no DOM. Server-friendly: Phase 13 SSR + Phase 15 PDF
// print path re-import from here. RESEARCH OQ-6 lean confirmed: lift the
// evaluator into packages/shared so server callers don't carry a fictitious
// apps/web import.
//
// Origin: this is a verbatim port of apps/web/src/widgets/summary-checklist/visible-when.ts
// (Phase 6 D-04 minimal predicate evaluator). The only change vs the source:
// the `sourceState` parameter is widened from the React-tier `WidgetState`
// union to a structural type — `{ readonly [key: string]: unknown } | undefined`.
// This drops the apps/web import without changing behavior: the function does
// its own runtime narrowing (`"selected" in sourceState`), so the type only
// needs to be wide enough to admit any object shape that callers in
// apps/web (every WidgetState variant), Phase 13 SSR, or Phase 15 PDF may
// pass. The function's runtime check rejects shapes that lack a `selected`
// array — matching Phase 6's `WidgetState`-typed behavior exactly.
//
// Phase 9 will generalize the `predicate` parameter to the full visibility
// Constraint AST (D-15) without changing the boolean-return contract — callers
// continue to import `isVisible` and get a stable signature.

import type { VisibleWhen } from "../schemas/widget-templates/summary-checklist.js";

// `selected?: unknown` (known optional property) keeps the property accessible
// via `.selected` under D-15 noPropertyAccessFromIndexSignature; the trailing
// index signature widens the type to admit any other-keyed object shape that
// callers in apps/web (every WidgetState variant) or Phase 13 SSR may pass.
export function isVisible(
  predicate: VisibleWhen | undefined,
  sourceState: ({ selected?: unknown } & { readonly [key: string]: unknown }) | undefined,
): boolean {
  if (predicate === undefined) return true; // no predicate → always visible
  if (sourceState === undefined) return false; // predicate present, source unmounted → hide-by-default
  // Phase 6 only supports option-set source state. The check is shape-narrowing:
  if (!("selected" in sourceState)) return false;
  const selected = sourceState.selected;
  if (!Array.isArray(selected)) return false;
  const selectedArr = selected as ReadonlyArray<string>;
  return predicate.anyOf.some((id) => selectedArr.includes(id));
}
